import { LoadingOverlay, ScrollArea } from '@mantine/core';
import { useForm } from '@mantine/form';
import { useDisclosure } from '@mantine/hooks';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { getRobot, updateRobotConfigs } from '@mygpt/app';

import { useGPTStore } from '@/store';
import { deepMerge } from '@/utils/helper';
import { useSubmitFormAsync } from '@/hooks/useSubmitFormAsync';
import { DEFAULT_WIDGET_CONFIGS } from '@/components/Widget/Embed';
import DigitalHumanSettings from './DigitalHumanSettings';

const DEFAULT_DIGITAL_HUMAN_CONFIGS = {
  enable_2d_digital_person: false,
  digitalHuman: {
    backgroundColor: {
      checked: true,
      value: '#C5C9EE',
    },
    messageBackgroundOpacity: 50,
    image: 'sparticle',
    textureUrl: undefined,
    playWelcomeMessage: {
      enabled: true,
      mode: 'first',
    },
    defaultInputMode: 'text',
  },
};

export default () => {
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const { setRobotInfo: setStoreRobotInfo, robotInfo } = useGPTStore();
  const [viewChange, setViewChange] = useState(0);
  const [mainWrapRight, setMainWrapRight] = useState(0);
  const [digitalHumanConfigs, setDigitalHumanConfigs] = useState(DEFAULT_DIGITAL_HUMAN_CONFIGS);
  const [loading, { close: closeLoading, open: openLoading }] = useDisclosure(false);

  const mainWrapRef = useRef<HTMLElement>(null);
  const containerHeaderRef = useRef<HTMLDivElement>(null);

  const form = useForm({
    initialValues: DEFAULT_DIGITAL_HUMAN_CONFIGS,
  });

  const onChange = () => {
    setViewChange(viewChange + 1);
  };

  const calcMainDistance = () => {
    if (!mainWrapRef.current) return;
    const { left } = mainWrapRef.current.getBoundingClientRect();
    setMainWrapRight(left - 250);
  };

  const initData = async (id: string) => {
    if (!id) return;
    openLoading();
    const res = await getRobot({ aiId: id });
    setStoreRobotInfo(res.data);
    const { enable_2d_digital_person, widget_configs } = res.data.configs;
    closeLoading();

    const parsedWidgetConfigs = widget_configs ? JSON.parse(widget_configs) : {};
    const serverDigitalHuman = parsedWidgetConfigs.digitalHuman || {};

    const configs = {
      enable_2d_digital_person: enable_2d_digital_person || false,
      digitalHuman: {
        ...DEFAULT_DIGITAL_HUMAN_CONFIGS.digitalHuman,
        ...serverDigitalHuman,
        // 确保textureUrl字段正确处理：只有当真正存在有效值时才保留
        textureUrl:
          serverDigitalHuman.textureUrl && serverDigitalHuman.textureUrl.trim() !== ''
            ? serverDigitalHuman.textureUrl
            : undefined,
      },
    };

    const digitalHumanConfigs = deepMerge(DEFAULT_DIGITAL_HUMAN_CONFIGS, configs);
    form.setValues(digitalHumanConfigs);
    setDigitalHumanConfigs(digitalHumanConfigs);
    onChange();
  };

  const submit = async () => {
    if (!id || !robotInfo) return;
    openLoading();
    try {
      const { enable_2d_digital_person, digitalHuman } = form.values;

      // 使用store中已有的机器人配置，如果没有则使用默认的widget配置
      const existingWidgetConfigs = robotInfo.configs.widget_configs
        ? JSON.parse(robotInfo.configs.widget_configs)
        : DEFAULT_WIDGET_CONFIGS;

      // 合并现有配置和新的数字人配置
      const updatedWidgetConfigs = {
        ...existingWidgetConfigs,
        digitalHuman: digitalHuman,
      };

      // 构建配置对象，保留其他widget设置
      const configs = {
        enable_2d_digital_person,
        widget_configs: JSON.stringify(updatedWidgetConfigs),
      } as any;

      await updateRobotConfigs({ aiId: id, configs });

      // 更新本地状态
      setDigitalHumanConfigs(form.values);
    } catch (error) {
      console.error('save digital human configs failed:', error);
    } finally {
      closeLoading();
    }
  };

  const submitInNextTick = useSubmitFormAsync(submit);

  const onOverrideChangeForForm = (prop: string) => (data?: any) => {
    if (data !== undefined) {
      form.setFieldValue(prop, data);
    } else {
      // 对于嵌套属性，需要特殊处理
      const keys = prop.split('.');
      let currentValue: any = form.values;
      for (const key of keys) {
        currentValue = currentValue[key];
      }
      form.setFieldValue(prop, !currentValue);
    }
    onChange();

    // 自动保存
    submitInNextTick();
  };

  useEffect(() => {
    if (!id) return;
    initData(id);
  }, [id]);

  useEffect(() => {
    calcMainDistance();
    window.addEventListener('resize', calcMainDistance);

    return () => {
      window.removeEventListener('resize', calcMainDistance);
    };
  }, []);

  return (
    <section className="flex gap-10 h-full" ref={mainWrapRef}>
      <section className="flex-auto relative min-w-[500px]">
        <ScrollArea.Autosize mah={`calc(100% - ${containerHeaderRef.current?.clientHeight}px)`} offsetScrollbars>
          <form
            onChange={onChange}
            onSubmit={(e) => {
              e.preventDefault();
            }}
          >
            <DigitalHumanSettings form={form} onOverrideChangeForForm={onOverrideChangeForForm} />
          </form>
        </ScrollArea.Autosize>

        <LoadingOverlay visible={loading} overlayProps={{ radius: 'sm', blur: 2 }} />
      </section>
    </section>
  );
};
